import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import {
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import Select from 'react-select';
import { Form, Formik, Field } from 'formik';
import CustomRadio from '../../../common/ui/CustomRadio';
import FormFieldWithLabel from '../../common/FormFieldWithLabel';
import { filterFieldsForForm } from 'drapcode-constant';
import * as moment from 'moment';
import * as Yup from 'yup';
import { dynamicSort, isProjectReadOnly } from '../../../common/utils/api_utils';
import { FieldDetail } from './FieldDetail';
import AddNewField from './AddNewField';
import { filterCollectionDefaultFields } from '../../common/FieldTypes';
import CustomLoader from '../../common/CustomLoader';
import { drapcodeIcon, iconForField } from '../../../common/custom/icon-library';
import showAlert from '../../../common/utils/alert';
import {
  extractDefaultCollectionFields,
  pluginDefaultFields,
  renderDefaultFieldType,
} from '../../../common/utils/field-options';
import TooltipInfo from '../../../common/docs/TooltipInfo';
import { description } from '../../../common/utils/event_constants';
import { showAlertHtml } from '../../../common/utils/alert';
import { filterNotAllowedCollections } from '../../../common/utils/constant';
import CollectionPHIAuditModalView from '../CollectionPHIAuditModalView';

class FieldSection extends Component {
  static propTypes = {
    actions: PropTypes.object.isRequired,
    commonActions: PropTypes.object.isRequired,
    collection: PropTypes.object.isRequired,
    projectCollections: PropTypes.array.isRequired,
  };

  state = {
    isAddNewField: false,
    snapshotName: `Snapshot-${moment().format('YYYY-MMMM-DD-HH-mm-ss-SSS')}`,
    openCrudModal: false,
    openPHIAuditModal: false,
    phiAuditResult: null,
  };

  createValidationSchema = () => {
    return Yup.object().shape({
      selectedPage: Yup.object()
        .shape({
          add: Yup.boolean(),
          list: Yup.boolean(),
          update: Yup.boolean(),
        })
        .test(
          'at-least-one-checked',
          'At least one page is required',
          (value) => value.add || value.list || value.update,
        ),
    });
  };
  componentDidMount() {
    const { collectionName } = this.props.collection;
    this.setState({
      snapshotName: `${collectionName}-Snapshot-${moment().format('YYYY-MMMM-DD-HH-mm-ss-SSS')}`,
    });
  }
  addNewField = () => {
    this.setState({ isAddNewField: true });
  };

  closeNewField = () => {
    this.setState({ isAddNewField: false });
  };

  collectionOptions = (isBelongTo = false) => {
    const { projectCollections, collection } = this.props;
    let collectionToSelect = projectCollections;
    if (isBelongTo) {
      collectionToSelect = projectCollections.filter(
        (e) => e.collectionName !== collection.collectionName,
      );
    }
    return collectionToSelect.sort(dynamicSort('collectionTitle')).map((coll, index) => {
      return (
        <option key={index} value={coll.collectionName}>
          {coll.collectionTitle}
        </option>
      );
    });
  };

  onCollectionDelete = () => {
    const {
      collection,
      currentCollectionListPage,
      projectId,
      actions,
      history,
      totalCollectionPages,
      version,
      homeActions,
    } = this.props;
    const preConfirm = async () => {
      await actions.deleteCollection({
        projectId: projectId,
        version,
        collectionId: collection.uuid,
        page: currentCollectionListPage,
        totalPages: totalCollectionPages,
      });
      homeActions.loadEditorCollections({ projectId, version });
      history.push(`/collections/${version}/v/${projectId}`);
    };
    showAlert("You won't be able to revert this.", preConfirm, 'Yes, delete it!');
  };

  handleSnapshot = () => {
    const { snapshotName } = this.state;
    if (snapshotName === '' || !snapshotName) {
      this.setState({ snapshotNameError: 'This field is required' });
      return;
    }

    const preConfirm = () => {
      const { projectId, actions, collection, version } = this.props;

      let collectionSnapshotValues = {
        name: snapshotName,
        projectId: projectId,
        collectionId: this.props.collection.uuid,
      };
      actions.createCollectionSnapshot({
        projectId,
        version,
        data: collectionSnapshotValues,
      });
    };
    showAlert('Are you sure you want to create a snapshot?', preConfirm, 'Yes, Create it!');
  };

  openCrudModal = () => {
    this.setState({ openCrudModal: true });
  };

  toggleOpenCrudModal = () => {
    this.setState({ openCrudModal: !this.state.openCrudModal });
  };

  handleSubmit = async (values) => {
    this.setState({ openCrudModal: !this.state.openCrudModal });
    await this.generateCRUDPages(values);
  };

  runPHIAudit = async () => {
    const { actions, collection, projectId, version } = this.props;
    this.setState({
      openPHIAuditModal: true,
    });
    const response = await actions.runCollectionPHIAudit({
      projectId,
      version,
      collectionName: collection.collectionName,
    });
    if (response && response.data && response.data.code === 200 && response.data.data) {
      this.setState({
        phiAuditResult: response.data.data,
      });
    }
  };

  openPHIAuditModal = async () => {
    const { actions, collection, projectId, version } = this.props;
    const { collectionName } = collection;
    this.setState({
      openPHIAuditModal: true,
    });
    const response = await actions.loadCollectionPHIAudit({
      projectId,
      version,
      collectionName: collectionName,
    });
    if (response && response.data && response.data.code === 200 && response.data.data) {
      this.setState({
        phiAuditResult: response.data.data,
      });
    } else {
      this.setState({
        phiAuditResult: null,
      });
    }
  };

  toggleOpenPHIAuditModal = () => {
    this.setState((prevState) => ({
      openPHIAuditModal: !prevState.openPHIAuditModal,
    }));
  };

  renderPHIAuditModalView = () => {
    const { collection, collections, projectId } = this.props;
    const { runCollectionPHIAuditPending } = collections;
    const { phiAuditResult } = this.state;
    const isReadOnly = isProjectReadOnly(projectId);
    return (
      <CollectionPHIAuditModalView
        collection={collection}
        phiAuditResult={phiAuditResult}
        runCollectionPHIAuditPending={runCollectionPHIAuditPending}
        runPHIAudit={this.runPHIAudit}
        isReadOnly={isReadOnly}
      />
    );
  };

  parseCrudModalView = (layouts) => {
    const { collectionTitle } = this.props.collection;
    let layoutOptions = layouts.map((layout) => ({ value: layout.uuid, label: layout.name }));
    return (
      <Formik
        initialValues={{
          selectedLayout: [],
          selectedPage: {
            list: false,
            update: false,
            add: false,
          },
        }}
        validationSchema={this.createValidationSchema()}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          this.handleSubmit(values);
          setSubmitting(false);
          resetForm();
        }}
      >
        {({ values, errors, setFieldValue }) => (
          <Form className="form-horizontal">
            <div className="form-group">
              <FormFieldWithLabel
                cta={false}
                label="Select Layout"
                icon="select"
                infoIcon={true}
                infoDescription={`Select a layout from the provided options to bind the CRUD operations`}
              >
                <Select
                  isMulti
                  options={layoutOptions}
                  onChange={(option) => setFieldValue('selectedLayout', option)}
                  className="custom-select-container"
                  classNamePrefix="custom-select"
                  value={values.selectedLayout}
                />
              </FormFieldWithLabel>
            </div>
            <div className="form-group">
              <FormFieldWithLabel label="Select Page " icon="toggle" isRequired={true}>
                <div className="d-flex align-items-center mt-2">
                  <div className="mr-4">
                    <div className="input-group" title={`${collectionTitle} List`}>
                      <div className="input-group-prepend"></div>
                      <div className="form-check">
                        <CustomRadio label={`${collectionTitle} List`} isRadio={false}>
                          <Field
                            type="checkbox"
                            name="selectedPage.list"
                            className="form-check-input"
                          />
                        </CustomRadio>
                      </div>
                    </div>
                  </div>
                  <div className="mr-4">
                    <div className="input-group" title={`Update ${collectionTitle}`}>
                      <div className="input-group-prepend"></div>
                      <div className="form-check">
                        <CustomRadio label={`Update ${collectionTitle}`} isRadio={false}>
                          <Field
                            type="checkbox"
                            name="selectedPage.update"
                            className="form-check-input"
                          />
                        </CustomRadio>
                      </div>
                    </div>
                  </div>
                  <div className="mr-4">
                    <div className="input-group" title={`Add ${collectionTitle}`}>
                      <div className="input-group-prepend"></div>
                      <div className="form-check">
                        <CustomRadio label={`Add ${collectionTitle}`} isRadio={false}>
                          <Field
                            type="checkbox"
                            name="selectedPage.add"
                            className="form-check-input"
                          />
                        </CustomRadio>
                      </div>
                    </div>
                  </div>
                </div>
              </FormFieldWithLabel>
              {errors.selectedPage && typeof errors.selectedPage === 'string' ? (
                <div className="text-danger" style={{ marginLeft: '2rem' }}>
                  {errors.selectedPage}
                </div>
              ) : null}
            </div>
            <div className="d-flex justify-content-between modal-footer">
              <button
                type="button"
                className="btn btn-xs btn-theme-default-outlined"
                onClick={this.toggleOpenCrudModal}
              >
                <span>
                  <i className={`wb webconnect-times`}></i>&nbsp;Cancel
                </span>
              </button>
              <button type="submit" className="btn btn-xs btn-theme-submit">
                <span>
                  {' '}
                  <i className={`wb webconnect-skip-end-circle-fill`}></i>&nbsp;Proceed
                </span>
              </button>
            </div>
          </Form>
        )}
      </Formik>
    );
  };

  generateCRUDPages = (values) => {
    const { actions, projectId, collection, version, homeActions } = this.props;
    const body = {
      selectedPage: values.selectedPage,
      selectedLayout: values.selectedLayout,
      collectionUuid: collection.uuid,
    };

    const confirmationMsg = `Do you want to proceed? <br/> This will generate dynamic pages with all the necessary actions you've selected and bind them to the layout if chosen.`;
    const preConfirm = async () => {
      await actions.generateCrudPages({
        projectId,
        collectionName: collection.collectionTitle,
        version,
        body,
      });

      await homeActions.loadPages({ projectId, version });
    };
    const confirmButtonText = 'Yes, Generate';
    showAlertHtml(confirmationMsg, preConfirm, confirmButtonText);
  };

  showPublishedMessage = () => {
    const { updatedCollections, collection } = this.props;
    const { uuid } = collection;
    if (updatedCollections && updatedCollections.includes(uuid)) {
      return <span className="fw-lighter unpublished-tag text-warning">(Not Published)</span>;
    }
    return '';
  };

  render() {
    const {
      collection,
      projectCollections,
      actions,
      commonActions,
      showCollectionFieldLoader,
      showCollectionFieldDeleteLoader,
      projectId,
      version,
      showCollectionDeleteLoader,
      projectDetail,
      collectionFlag,
      fieldFlag,
      collections,
      snippets,
      roles,
    } = this.props;
    const { uuid, isPluginCollection, restrictEdit, fields, collectionTitle, collectionName } =
      collection;
    const { runCollectionPHIAuditPending, generateCrudPagesPending } = collections;
    const cFields = filterFieldsForForm(fields);
    const defaultCollectionFields = filterCollectionDefaultFields(fields);

    pluginDefaultFields.forEach((fieldName) =>
      extractDefaultCollectionFields(fields, fieldName, defaultCollectionFields),
    );

    const userIdField =
      collection.isUserSettingCollection &&
      collection.fields &&
      collection.fields.find((field) => field.isPluginField && field.fieldName === 'userId');
    if (userIdField) {
      defaultCollectionFields.push({
        order: defaultCollectionFields.length + 1,
        defaultField: userIdField,
      });
    }

    const layouts = snippets.filter(
      (snippet) => snippet.snippetType === 'PAGE_LAYOUT' && snippet.role && snippet.role !== '',
    );
    const isReadOnly = isProjectReadOnly(projectId);
    return (
      <>
        <div className="card-title-header">
          <div>
            <h5 className="card-title pb-0 mb-0">
              {collectionTitle} Fields {this.showPublishedMessage()}
            </h5>
            <p>
              Add different fields to segregate data under different headings.
              <br />
              <small>
                <span className="required">*</span>Note: Please DO NOT create any of the system
                default Collection fields to avoid conflicts.
              </small>
            </p>
          </div>
          <div className="card-cta">
            <UncontrolledDropdown group className="custom-pad-right-1 pr-2">
              <DropdownToggle color="theme-warning" title="Modify" className="btn-md" caret>
                <i className="wb webconnect-cogs"></i> Modify
              </DropdownToggle>
              <DropdownMenu>
                {restrictEdit ? (
                  <></>
                ) : (
                  <>
                    <DropdownItem
                      onClick={() => {
                        if (isReadOnly) return;
                        actions.toggleCollectionUpdate();
                        this.props.toggleField(false);
                      }}
                      className="theme-green"
                      disabled={isReadOnly}
                      style={{
                        cursor: isReadOnly ? 'not-allowed' : 'pointer',
                        opacity: isReadOnly ? 0.5 : 1,
                      }}
                    >
                      <i className="wb webconnect-pencil"></i>&nbsp;Update Collection Details
                    </DropdownItem>
                  </>
                )}
                <DropdownItem
                  onClick={() => {
                    actions.toggleCollectionUpdate();
                    this.props.toggleField(true);
                  }}
                  className="theme-green"
                >
                  <i className="wb webconnect-eye"></i>&nbsp;Show Fields
                </DropdownItem>
                <DropdownItem
                  onClick={isReadOnly ? undefined : this.handleSnapshot}
                  disabled={isReadOnly}
                  className="theme-green"
                  style={{
                    cursor: isReadOnly ? 'not-allowed' : 'pointer',
                    opacity: isReadOnly ? 0.5 : 1,
                  }}
                >
                  <i className="wb webconnect-camera-fill"></i>&nbsp;Create Snapshot
                </DropdownItem>
                {/**
                 * Always keep the Delete option at the bottom
                 * */}
                {uuid && !isPluginCollection && (
                  <DropdownItem
                    disabled={showCollectionDeleteLoader || isReadOnly}
                    onClick={isReadOnly || !collectionFlag ? undefined : this.onCollectionDelete}
                    className="theme-red"
                    style={{
                      cursor: isReadOnly || !collectionFlag ? 'not-allowed' : 'pointer',
                      opacity: isReadOnly ? 0.5 : 1,
                    }}
                  >
                    <i className="wb webconnect-trash-regular"></i>
                    &nbsp;Delete Collection
                    {collectionFlag ? null : (
                      <>
                        &nbsp;
                        <TooltipInfo
                          title={description}
                          headerText={`Permission Disabled!`}
                          showHeader={true}
                          showArrow={true}
                        />
                      </>
                    )}
                    <CustomLoader loading={showCollectionDeleteLoader} />
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
            {filterNotAllowedCollections.includes(collectionName) ? (
              ''
            ) : (
              <button
                type="button"
                className="btn btn-theme-submit btn-md"
                onClick={isReadOnly ? undefined : this.addNewField}
                disabled={isReadOnly}
              >
                <i className={`wb ${drapcodeIcon('add')}`}></i> Add Field&nbsp;
                <CustomLoader loading={showCollectionFieldLoader} />
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary btn-md ml-2"
              onClick={isReadOnly ? undefined : this.openCrudModal}
              disabled={generateCrudPagesPending || isReadOnly ? 'disabled' : ''}
            >
              <i className="wb webconnect-journal-plus"></i> Generate CRUD Pages&nbsp;
              <CustomLoader loading={generateCrudPagesPending} />
            </button>
            <Modal
              isOpen={this.state.openCrudModal}
              toggle={this.toggleOpenCrudModal}
              backdrop="static"
              size="lg"
            >
              <ModalHeader toggle={this.toggleOpenCrudModal} className="border-bottom">
                Select Layout and Pages you want to add
              </ModalHeader>
              <ModalBody>{this.parseCrudModalView(layouts)}</ModalBody>
            </Modal>
            <button
              type="button"
              className="btn btn-primary btn-md ml-2"
              onClick={this.openPHIAuditModal}
            >
              <i className={`wb webconnect-shield-check`}></i> PHI Audit&nbsp;
            </button>
            <Modal
              isOpen={this.state.openPHIAuditModal}
              toggle={this.toggleOpenPHIAuditModal}
              backdrop="static"
              size="lg"
            >
              <ModalHeader
                toggle={this.toggleOpenPHIAuditModal}
                className="border-bottom"
                style={{ position: 'relative' }}
              >
                <div>
                  <div className="d-flex align-items-center">
                    <i className="wb webconnect-search mr-2"></i>
                    {collectionTitle} PHI Audit
                  </div>

                  {this.state.phiAuditResult && this.state.phiAuditResult.auditedAt && (
                    <small className="text-muted d-block mt-1">
                      Last audited: {new Date(this.state.phiAuditResult.auditedAt).toLocaleString()}
                    </small>
                  )}
                </div>

                {this.state.phiAuditResult && (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={isReadOnly ? undefined : this.runPHIAudit}
                    disabled={runCollectionPHIAuditPending || isReadOnly}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '45px',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    {runCollectionPHIAuditPending ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-1"
                          role="status"
                          aria-hidden="true"
                        />
                        Auditing...
                      </>
                    ) : (
                      <>
                        <i className="wb webconnect-refresh mr-1"></i>
                        Run Audit Again
                      </>
                    )}
                  </button>
                )}
              </ModalHeader>

              <ModalBody>{this.renderPHIAuditModalView()}</ModalBody>
            </Modal>
          </div>
        </div>

        <Accordion allowZeroExpanded preExpanded={['newField']}>
          <AccordionItem uuid="newField">
            <AddNewField
              addNewField={this.addNewField}
              closeNewField={this.closeNewField}
              isAddNewField={this.state.isAddNewField}
              actions={actions}
              projectCollections={projectCollections}
              collection={collection}
              projectId={projectId}
              version={version}
              collectionOptions={this.collectionOptions}
              showCollectionFieldDeleteLoader={showCollectionFieldDeleteLoader}
              projectDetail={projectDetail}
              fieldFlag={fieldFlag}
              roles={roles}
            />
          </AccordionItem>
        </Accordion>

        <Accordion allowZeroExpanded>
          {defaultCollectionFields instanceof Array &&
            defaultCollectionFields
              .sort((a, b) => a.order - b.order)
              .map((fieldObj, key) => (
                <AccordionItem
                  uuid={`defaultKey${key}`}
                  key={`defaultKey${key}`}
                  dangerouslySetExpanded={false}
                >
                  <AccordionItemHeading>
                    <AccordionItemButton>
                      <i className={`wb ${iconForField(fieldObj.defaultField.type)}`}></i>{' '}
                      <strong>{fieldObj.defaultField.fieldTitle.en}</strong>{' '}
                      {renderDefaultFieldType(fieldObj.defaultField.fieldName)}
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel></AccordionItemPanel>
                </AccordionItem>
              ))}
          {cFields instanceof Array &&
            cFields
              .filter((field) => {
                const conditionallyFilterOut = collection.isUserSettingCollection ? ['userId'] : [];
                return ![...pluginDefaultFields, ...conditionallyFilterOut].includes(
                  field.fieldName,
                );
              })
              .map((field, key) => (
                <AccordionItem uuid={key} key={key}>
                  <FieldDetail
                    actions={actions}
                    commonActions={commonActions}
                    key={field._id}
                    field={field}
                    collection={collection}
                    projectId={projectId}
                    version={version}
                    projectCollections={projectCollections}
                    collectionOptions={this.collectionOptions}
                    showCollectionFieldDeleteLoader={showCollectionFieldDeleteLoader}
                    projectDetail={projectDetail}
                    fieldFlag={fieldFlag}
                    roles={roles}
                  />
                </AccordionItem>
              ))}
        </Accordion>
      </>
    );
  }
}

export default FieldSection;
